import { LINE_LABELS } from './labels.js';
import { combineDiscounts, fxFactor, recurringYearlyRate, round2, skuForQty, travelFee, volumeTierPct } from './math.js';
import { BUDGET_OPTIONS, DISCOUNTABLE_LINES } from './types.js';
export function computeCounts(inputs, types) {
    let transmitters = 0;
    let configurableSensors = 0;
    let calibrated = 0;
    for (const item of inputs.equipment) {
        const type = types.get(item.typeKey);
        if (!type)
            continue;
        const sensors = item.qty * type.sensorsPerTransmitter;
        transmitters += item.qty;
        configurableSensors += sensors;
        if (item.calibrated)
            calibrated += sensors;
    }
    const gateways = inputs.connectivity.mode === 'gateway' ? inputs.connectivity.gateways : 0;
    const managedSensors = configurableSensors + inputs.thirdPartySensors;
    return { transmitters, gateways, configurableSensors, managedSensors, calibratedSensors: Math.min(calibrated, managedSensors) };
}
function sumBy(lines, category) {
    return round2(lines.filter((l) => l.category === category).reduce((sum, l) => sum + l.total, 0));
}
function perSensor(total, managedSensors) {
    return managedSensors > 0 ? round2(total / managedSensors) : 0;
}
function summarize(key, label, options, discountPct, lines, managedSensors, contingencyPct) {
    const hardware = sumBy(lines, 'hardware');
    const setup = sumBy(lines, 'services');
    const oneTime = round2(hardware + setup);
    const subscription = sumBy(lines, 'recurring');
    const factor = 1 + contingencyPct / 100;
    const year1 = round2((oneTime + subscription) * factor);
    const year5 = round2((oneTime + 5 * subscription) * factor);
    return {
        key, label, options, discountPct, lines, hardware, setup, oneTime, subscription, year1, year5,
        recurringPerSensor: perSensor(subscription, managedSensors),
        perSensorY1: perSensor(year1, managedSensors),
        perSensorY5: perSensor(year5, managedSensors),
    };
}
export function priceMonitoring(inputs, book) {
    const rates = book.rates;
    const { services, recurring, shipping } = rates;
    const fx = fxFactor(inputs.currency, inputs.fxRate, rates.fxMarkupPct);
    const types = new Map(rates.equipment.map((t) => [t.key, t]));
    const counts = computeCounts(inputs, types);
    const managed = counts.managedSensors;
    const calibrated = counts.calibratedSensors;
    const devices = counts.transmitters + counts.gateways;
    const { km } = inputs;
    const dataPlans = inputs.connectivity.mode === 'gateway' ? inputs.connectivity.dataPlans : 0;
    const hardwareTier = volumeTierPct(rates.volumeTiers.hardware, counts.transmitters);
    const overrides = inputs.lineDiscounts;
    const lineDiscount = {
        gateway: overrides.gateway ?? 0,
        installation: overrides.installation ?? volumeTierPct(rates.volumeTiers.services, devices),
        configuration: overrides.configuration ?? volumeTierPct(rates.volumeTiers.services, counts.configurableSensors),
        iqoq: overrides.iqoq ?? 0,
        travel: overrides.travel ?? 0,
        shipping: overrides.shipping ?? 0,
    };
    const shippingFee = km === null
        ? 0
        : Math.max(shipping.base, (shipping.base * devices) / shipping.devicesPerBase) * Math.max(1, km / shipping.kmPerMultiplier);
    const makeLine = (spec, budgetPct) => {
        const unitPrice = round2(spec.unitCad * fx);
        const discountPct = combineDiscounts(spec.linePct ?? 0, budgetPct);
        return {
            key: spec.key, sku: spec.sku, label: spec.label, category: spec.category, qty: spec.qty, unitPrice, discountPct,
            kind: spec.category === 'recurring' ? 'recurring' : 'one_time',
            total: round2(spec.qty * unitPrice * (1 - discountPct / 100)),
        };
    };
    const priceBudget = (key, label, options, budgetPct) => {
        const lines = [];
        const add = (spec) => { if (spec.qty > 0)
            lines.push(makeLine(spec, budgetPct)); };
        const tiered = (lineKey, qty) => ({
            key: lineKey, sku: skuForQty(recurring[lineKey].skuTiers, qty), label: LINE_LABELS[lineKey],
            category: 'recurring', qty, unitCad: recurringYearlyRate(recurring[lineKey], qty),
        });
        for (const item of inputs.equipment) {
            const type = types.get(item.typeKey);
            if (!type)
                continue;
            add({
                key: `equipment:${type.key}`, sku: type.sku, label: type.label, category: 'hardware',
                qty: item.qty, unitCad: type.unitPrice, linePct: item.discountPct ?? hardwareTier,
            });
        }
        add({
            key: 'gateway', sku: rates.gateway.sku, label: LINE_LABELS.gateway, category: 'hardware',
            qty: counts.gateways, unitCad: rates.gateway.price, linePct: lineDiscount.gateway,
        });
        if (options.installation) {
            const { sku, price } = services.installationPerDevice;
            add({
                key: 'installation', sku, label: LINE_LABELS.installation, category: 'services',
                qty: devices, unitCad: price, linePct: lineDiscount.installation,
            });
        }
        if (options.configuration) {
            const { sku, price } = services.configurationPerSensor;
            add({
                key: 'configuration', sku, label: LINE_LABELS.configuration, category: 'services',
                qty: counts.configurableSensors, unitCad: price, linePct: lineDiscount.configuration,
            });
        }
        if (options.iqoq) {
            const { sku, price } = services.iqoqPerSensor;
            add({ key: 'iqoq', sku, label: LINE_LABELS.iqoq, category: 'services', qty: managed, unitCad: price, linePct: lineDiscount.iqoq });
        }
        if (devices > 0 && options.installation) {
            const unitCad = km === null ? 0 : travelFee(rates.travel, km);
            const spec = {
                key: 'travel', sku: rates.travel.sku, label: LINE_LABELS.travel, category: 'services', qty: 1, unitCad, linePct: lineDiscount.travel,
            };
            lines.push(makeLine(spec, budgetPct));
        }
        if (devices > 0 && !options.installation) {
            const spec = {
                key: 'shipping', sku: shipping.sku, label: LINE_LABELS.shipping, category: 'services',
                qty: 1, unitCad: shippingFee, linePct: lineDiscount.shipping,
            };
            lines.push(makeLine(spec, budgetPct));
        }
        add(tiered('hosting', managed));
        add(tiered('support', managed));
        add(tiered('warranty', managed));
        add({
            key: 'dataPlan', sku: skuForQty(rates.dataPlan.skuTiers, dataPlans), label: LINE_LABELS.dataPlan, category: 'recurring',
            qty: dataPlans, unitCad: rates.dataPlan.yearlyPrice,
        });
        if (options.calibration) {
            add(tiered('calibration', calibrated));
            add({
                key: 'calibrationTravel', sku: rates.calibrationTravel.sku, label: LINE_LABELS.calibrationTravel, category: 'recurring',
                qty: inputs.calibrationVisitsPerYear, unitCad: km === null ? 0 : travelFee(rates.calibrationTravel, km),
            });
            add({
                key: 'calibrationShipping', sku: shipping.sku, label: LINE_LABELS.calibrationShipping, category: 'recurring',
                qty: inputs.calibrationShipmentsPerYear, unitCad: shipping.calibrationPerShipment,
            });
        }
        return summarize(key, label, options, budgetPct, lines, managed, inputs.contingencyPct);
    };
    const budgets = inputs.budgets.map((b) => priceBudget(b.key, b.label, b.options, b.discountPct));
    const union = Object.fromEntries(BUDGET_OPTIONS.map((o) => [o, inputs.budgets.some((b) => b.options[o])]));
    const fullScope = priceBudget('full', 'full', union, 0);
    const warnings = [];
    if (counts.transmitters === 0)
        warnings.push({ code: 'no_equipment', blocking: true });
    if (km === null && devices > 0)
        warnings.push({ code: 'km_missing', blocking: true });
    for (const b of inputs.budgets) {
        if (b.options.calibration && calibrated === 0) {
            warnings.push({ code: 'calibration_without_calibrated_sensors', blocking: false, budgetKey: b.key });
        }
    }
    const limits = rates.discountLimits;
    const approvalReasons = [];
    for (const item of inputs.equipment) {
        const pct = item.discountPct ?? hardwareTier;
        if (item.qty > 0 && pct > limits.hardware) {
            approvalReasons.push({
                code: 'line_discount_over_limit', category: 'hardware', key: `equipment:${item.typeKey}`, discountPct: pct, limitPct: limits.hardware,
            });
        }
    }
    // A line's discount only matters when some budget actually contains that line.
    const lineUsed = {
        gateway: counts.gateways > 0,
        installation: inputs.budgets.some((b) => b.options.installation),
        configuration: inputs.budgets.some((b) => b.options.configuration),
        iqoq: inputs.budgets.some((b) => b.options.iqoq),
        travel: devices > 0 && inputs.budgets.some((b) => b.options.installation),
        shipping: devices > 0 && inputs.budgets.some((b) => !b.options.installation),
    };
    for (const key of DISCOUNTABLE_LINES) {
        const category = key === 'gateway' ? 'hardware' : 'services';
        const limitPct = limits[category];
        if (lineUsed[key] && lineDiscount[key] > limitPct) {
            approvalReasons.push({ code: 'line_discount_over_limit', category, key, discountPct: lineDiscount[key], limitPct });
        }
    }
    for (const b of inputs.budgets) {
        if (b.discountPct > limits.budget) {
            approvalReasons.push({
                code: 'budget_discount_over_limit', category: 'budget', key: b.key, discountPct: b.discountPct, limitPct: limits.budget,
            });
        }
    }
    return {
        offerType: 'monitoring', priceBookId: book.id, priceBookVersion: book.version, currency: inputs.currency,
        counts, budgets, fullScope, warnings, approvalReasons,
    };
}
//# sourceMappingURL=monitoring.js.map